import { css, html, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { waitFor } from '../modules/async';
import { debounce, documentContains } from '../modules/common';
import { KryptonElement } from './krypton-element';
import { NotificationElement } from './notification-element';
import { ICONS } from '../modules/resources';
import { KryptonEventDetail } from './krypton-event-detail';
import { KryptonConstants } from '../modules/krypton_constants';


export class NotificationBoard extends KryptonElement {

    _opened = false;

    get opened() {
        return this._opened;
    }

    @property({ type: Boolean, reflect: true })
    set opened(opened) { //TODO possibly debounce opened=true
        const oldValue = this._opened;
        this._opened = opened;
        if (opened) {
            this.classList.add("opened");
            if (this.lastFocused = document.activeElement as HTMLElement) {
                this.lastFocused.blur();
            }
            this.async(this.showAll.bind(this), 150);
            if (this.notifications.filter(n => n.blocking).length) {
                this.fire(KryptonEventDetail.NOTIFICATION_SHOW_HIDE_EVENT, new KryptonEventDetail(KryptonEventDetail.SHOW, KryptonEventDetail.NOTIFICATION_SHOW_HIDE_EVENT, {}));
            }
        } else {
            this.classList.remove("opened");
            if (this.lastFocused) {
                documentContains(this.lastFocused) && this.lastFocused.focus();
                this.lastFocused = null;
            }
            this.async(this.hideAll.bind(this), 150);
            this.fire(KryptonEventDetail.NOTIFICATION_SHOW_HIDE_EVENT, new KryptonEventDetail(KryptonEventDetail.HIDE, KryptonEventDetail.NOTIFICATION_SHOW_HIDE_EVENT, {}));
        }

        this.requestUpdate("opened", oldValue);
    }

    _blocking = false;

    get blocking() {
        return this._blocking;
    }

    @property({ type: Boolean, reflect: true })
    set blocking(blocking) {
        const oldValue = this._blocking;
        this._blocking = blocking;
        this.classList[blocking ? "add" : "remove"]("blocking");
        this.requestUpdate("blocking", oldValue);
    }

    connectedCallback() {
        //-- global key event listeners
        window.addEventListener("keydown", e => {
            if (!this.opened) return;
            const key = e.which || e.keyCode || 0;
            switch (key) {
                case 27: //-- esc
                    this.dismissAll();
                    break;
            }
        });
        super.connectedCallback();
    }

    async dismiss(notification: NotificationElement) {
        notification.classList.remove("show");
        notification.classList.add("hide");
        this.async(() => {
            notification.parentElement?.removeChild(notification);
            this.async(() => this.tracker.delete(notification.hashCode()), 500);
            this.adjustScrollable();
        }, 350);
    }

    async dismissAll() {
        const notifications = this.notifications
            , duration = Math.log10(notifications.length) * KryptonConstants.notificationDismissMultiplier;
        let i = 0;
        notifications.forEach(n => this.async(() => this.dismiss(n), Math.log10(i++) * KryptonConstants.notificationDismissMultiplier));
        await waitFor(duration + KryptonConstants.notificationAnimationTime);
        this.opened = false
    }

    async showAll() {
        const notifications = this.notifications
            , duration = Math.log10(notifications.length) * KryptonConstants.notificationDismissMultiplier;
        let i = 0;
        notifications.forEach(n => this.async(() => {
            n.classList.remove("hide");
            n.classList.add("show");
        }, Math.log10(i++) * KryptonConstants.notificationDismissMultiplier));
        await waitFor(duration + KryptonConstants.notificationAnimationTime);
    }

    async hideAll() {
        const notifications = this.notifications
            , duration = Math.log10(notifications.length) * KryptonConstants.notificationDismissMultiplier;
        let i = 0;
        notifications.forEach(n => this.async(() => {
            n.classList.remove("show");
            n.classList.add("hide");
        }, Math.log10(i++) * KryptonConstants.notificationDismissMultiplier));
        await waitFor(duration + KryptonConstants.notificationAnimationTime);
    }

    onSlotChange(e: Event) {
        let notifications = this.notifications;

        notifications.forEach((n: NotificationElement) => {
            const hashCode = n.hashCode();
            if (this.tracker.has(hashCode)) {
                let existing = this.tracker.get(hashCode) as NotificationElement;
                if (existing != n) {
                    existing.count++;
                    this.notifications.splice(this.notifications.indexOf(n), 1);
                    n.remove();
                }
            } else {
                this.tracker.set(hashCode, n);
            }
        });

        if (notifications.filter(n => n.blocking).length) {
            this.classList.add("blocking");
        } else {
            this.classList.remove("blocking");
        }

        this.opened = !!notifications.length;
        this.adjustScrollable();
    }

    onNotificationElementEvent(e: CustomEvent) {
        if (e.detail.name === KryptonEventDetail.NOTIFICATION_ELEMENT_EVENT) {
            const notification = e.target as NotificationElement
                , detail = e.detail;
            if (detail.getProperties().dismiss) {
                this.dismiss(notification);
                return;
            }
        }
    }

    get notifications(): NotificationElement[] {
        //-- unnested slots
        //return [].slice.call(this.shadowRoot?.querySelector("slot")?.assignedNodes().filter(n => n instanceof NotificationElement));
        //-- nested slots
        // const notifications: NotificationElement[] = [];
        // const slots: HTMLSlotElement[] = [this.shadowRoot?.querySelector("slot") as HTMLSlotElement];
        // let slot;
        // while (slot = slots.shift()) {
        //     slot.assignedNodes().forEach(node => {
        //         if (node instanceof HTMLSlotElement) slots.push(node);
        //         if (node instanceof NotificationElement) notifications.push(node);
        //     });
        // }
        // return notifications;
        return this.getNestedSlotElements(this.shadowRoot?.querySelector("slot") as HTMLSlotElement, NotificationElement) as NotificationElement[];
    }

    transposeHorizontal() {
        this.classList.toggle("transpose-h");
        for (var i = 0, n, tmp = this.notifications.slice(0); n = tmp[i++];)
            n.classList.toggle("reverse");
    }

    transposeVertical() {
        this.classList.toggle("transpose-v");
    }

    private tracker = new Map<number, NotificationElement>();

    private lastFocused: HTMLElement | null;

    private adjustScrollable = debounce(() => {
        const container = this.shadowRoot?.getElementById("container") as HTMLElement;
        this.classList.add("scrollable");
        if (container.offsetHeight < container.scrollHeight) {
            this.classList.add("scrollable");
        } else {
            this.classList.remove("scrollable");
        }
    }, 75);

    static stylesHost = css`
        :host {
            position: fixed;
            top: 51px;
            top: var(--krypton-base-header-offset);
            left: 0;
            z-index: 9750;
            z-index: var(--z-index-notification-board);
            display: none;
            height: calc(100% - 51px);
            min-height: calc(100% - 51px);
            max-height: 100%;
            box-sizing: border-box;
        }

        :host(.opened.blocking) {
            width: 100%;
            min-width: 100%;
            height: 100%;
            min-height: 100%;
            overflow: hidden;
            background-color: rgba(0, 0, 0, 0.35);
            display: block;
        }

        :host(.opened) {
            display: block;
        }

        #container {
            display: flex;
            flex-direction: column;
            flex-flow: column;
            padding: 1rem 0;
            overflow-x: hidden;
            overflow-y: auto;
            height: calc(100vh - 60px);
            box-sizing: border-box;
            max-width: calc(var(--krypton-notification-width) + 20px);
        }

        :host(.scrollable) #container {
            padding-right: 17px;
        }

        ::slotted(*) {
            margin-bottom: 1em;
            transition: all 350ms ease-in;
            transform: translateX(-100%);
        }

        ::slotted(.show) {
            transform: translateX(0%);
        }

        ::slotted(.hide) {
            min-height: 0;
            height: 0;
            padding: 0;
            margin: 0;
            overflow: hidden;
        }`;

    static stylesControls = css`
        #controls {
            display: flex;
            flex: 0 0 auto;
            flex-direction: row;
            justify-content: flex-end;
            width: var(--krypton-notification-width);
            max-width: var(--krypton-notification-max-width);
            margin-bottom: var(--krypton-notification-spacing);
            padding: 0.25rem;
            background: -moz-linear-gradient(45deg, rgba(140, 140, 140, 0.1) 0%, rgba(70, 70, 70, .8) 100%); /* ff3.6+ */
            background: -webkit-gradient(linear, left bottom, right top, color-stop(0%, rgba(0, 0, 0, 0)), color-stop(100%, rgba(70, 70, 70, .8))); /* safari4+,chrome */
            background: -webkit-linear-gradient(45deg, rgba(140, 140, 140, 0.1) 0%, rgba(70, 70, 70, .8) 100%); /* safari5.1+,chrome10+ */
            background: -o-linear-gradient(45deg, rgba(140, 140, 140, 0.1) 0%, rgba(70, 70, 70, .8) 100%); /* opera 11.10+ */
            background: -ms-linear-gradient(45deg, rgba(140, 140, 140, 0.1) 0%, rgba(70, 70, 70, .8) 100%); /* ie10+ */
            background: linear-gradient(45deg, rgba(140, 140, 140, 0.1) 0%, rgba(70, 70, 70, .8) 100%); /* w3c */
            filter: progid:DXImageTransform.Microsoft.gradient(startColorstr='#8C8C8C', endColorstr='#000000', GradientType=1); /* ie6-9 */
            box-sizing: border-box;
        }

        .control {
            display: flex;
            color: white;
            font-size: 1.5rem;
            font-weight: normal;
            text-align: right;
            border: none;
            background: transparent;
            box-sizing: border-box;
            cursor: pointer;
        }

        .control:focus {
            outline: none;
        }

        .control::before {
            content: '';
            display: inline-block;
            height: 24px;
            width: 24px;
            background-size: contain;
        }
        @media only screen and (max-width: 767px) {
            .hidden-md-down {
                display:none;
            }
            .hidden-md-down::before {
                display:none;
            }
        }

        #dismiss::before {
            background: url('${unsafeCSS(ICONS.dismiss.file)}') left center no-repeat;
        }

        #transpose-h::before {
            background: url('${unsafeCSS(ICONS.swap.file)}') left center no-repeat;
        }

        :host(.transpose-h),
        :host(.transpose-h) #container {
            position: fixed;
            right: 0px;
        }

        :host(.transpose-v) #container {
            padding-bottom: 60px;
        }

        #transpose-v {
            transform: rotate(90deg);
        }

        #transpose-v::before {
            background: url('${unsafeCSS(ICONS.swap.file)}') left center no-repeat;
        }

        :host(.transpose-v) #controls {
            margin-top: auto;
        }`;

    static stylesScrollbar = css`
        #container {
            scrollbar-width: thin;
            scrollbar-color: rgba(85, 107, 123, 0.5);
        }

        :host::-webkit-scrollbar,
        #container::-webkit-scrollbar {
            width: 12px;
        }

        :host::-webkit-scrollbar-track,
        #container::-webkit-scrollbar-track {
            -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.7);
            box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.7);
            background-color: white;
        }

        :host::-webkit-scrollbar-thumb,
        #container::-webkit-scrollbar-thumb {
            border-radius: 10px;
            background-color: rgba(85, 107, 123, 0.5);
            -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.9);
            box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.9);
        }

        /* The following are from browserstrangeness.com */

        /* detects Chrome and Edge */
        @media screen and (-webkit-min-device-pixel-ratio: 0) {
            #container,
            :host(.scrollable) #container {
                padding-right: 0;
            }

            /* detects Edge */
            @supports (-ms-ime-align:auto) {
                #container,
                :host(.scrollable) #container {
                    padding-right: 17px;
                }
            }
        }
    `;

    static styles = [
        NotificationBoard.stylesHost,
        NotificationBoard.stylesControls,
        NotificationBoard.stylesScrollbar
    ];

    render() {
        return html`
        <div id="container" @notification-element-event="${this.onNotificationElementEvent}">
            <div id="controls">
                <button type="button" id="transpose-h" class="control hidden-md-down" @click="${this.transposeHorizontal}"></button>
                <button type="button" id="transpose-v" class="control hidden-md-down" @click="${this.transposeVertical}"></button>
                <button type="button" id="dismiss" class="control" @click="${this.dismissAll}">dismiss all</button>
            </div>
            <div>
                <slot @slotchange="${this.onSlotChange}"></slot>
            </div>
        </div>
        `;
    }

}

declare global {
    interface HTMLElementTagNameMap {
        'notification-board': NotificationBoard;
    }
}
